import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { DEFAULT_REVIEW_FLOW_CONFIG } from '../remote-config/reviewFlowConfig';
import type {
  ReviewFlowConfig,
  ReviewFlowTriggerKey,
  ReviewPersistedState,
  ReviewSentiment,
} from '../types';

interface ReviewState {
  reviewFlow: number;
  trustpilotUrl: string;
  configLoaded: boolean;
  reviewFlowConfig: ReviewFlowConfig;
  reviewFlowConfigLoaded: boolean;
  completed: boolean;
  shownCount: number;
  happyMomentCount: number;
  lastShownAt: number;
  lastSentiment: ReviewSentiment | null;
  stateLoaded: boolean;
  pendingReview: boolean;
  pendingFeatureName: string | null;
  blockers: string[];
}

const initialState: ReviewState = {
  reviewFlow: 0,
  trustpilotUrl: '',
  configLoaded: false,
  reviewFlowConfig: DEFAULT_REVIEW_FLOW_CONFIG,
  reviewFlowConfigLoaded: false,
  completed: false,
  shownCount: 0,
  happyMomentCount: 0,
  lastShownAt: 0,
  lastSentiment: null,
  stateLoaded: false,
  pendingReview: false,
  pendingFeatureName: null,
  blockers: [],
};

function isEligible(state: ReviewState): boolean {
  if (state.reviewFlow !== 1) return false;
  return !state.completed;
}

const reviewSlice = createSlice({
  name: 'review',
  initialState,
  reducers: {
    setReviewConfig: (
      state,
      action: PayloadAction<{ reviewFlow: number; trustpilotUrl: string }>
    ) => {
      state.reviewFlow = action.payload.reviewFlow;
      state.trustpilotUrl = action.payload.trustpilotUrl;
      state.configLoaded = true;
    },
    setReviewFlowConfig: (state, action: PayloadAction<ReviewFlowConfig>) => {
      state.reviewFlowConfig = action.payload;
      state.reviewFlowConfigLoaded = true;
    },
    hydrateReviewState: (
      state,
      action: PayloadAction<ReviewPersistedState>
    ) => {
      state.completed = action.payload.completed;
      state.shownCount = action.payload.shownCount;
      state.happyMomentCount = action.payload.happyMomentCount;
      state.lastShownAt = action.payload.lastShownAt;
      state.lastSentiment = action.payload.lastSentiment;
      state.stateLoaded = true;
    },
    resetReviewState: (state) => {
      state.completed = false;
      state.shownCount = 0;
      state.happyMomentCount = 0;
      state.lastShownAt = 0;
      state.lastSentiment = null;
      state.stateLoaded = true;
      state.pendingReview = false;
      state.pendingFeatureName = null;
    },
    registerHappyMoment: (state) => {
      state.happyMomentCount += 1;
    },
    registerTriggerHit: (
      state,
      action: PayloadAction<{
        trigger: ReviewFlowTriggerKey;
        count: number;
        featureName?: string | null;
      }>
    ) => {
      const { trigger, count, featureName } = action.payload;

      if (!state.reviewFlowConfig.enabled) return;

      const triggerConfig = state.reviewFlowConfig.triggers[trigger];
      if (!triggerConfig?.enabled) return;
      if (!triggerConfig.trigger_at.includes(count)) return;
      if (!isEligible(state)) return;

      state.pendingReview = true;
      if (featureName) {
        state.pendingFeatureName = featureName;
      }
    },
    clearPendingReview: (state) => {
      state.pendingReview = false;
    },
    blockReview: (state, action: PayloadAction<string>) => {
      if (!state.blockers.includes(action.payload)) {
        state.blockers.push(action.payload);
      }
    },
    unblockReview: (state, action: PayloadAction<string>) => {
      state.blockers = state.blockers.filter(
        (blocker) => blocker !== action.payload
      );
    },
    markShown: (state, action: PayloadAction<{ now: number }>) => {
      state.shownCount += 1;
      state.lastShownAt = action.payload.now;
      state.pendingReview = false;
    },
    setSentiment: (state, action: PayloadAction<ReviewSentiment>) => {
      state.lastSentiment = action.payload;
    },
    markCompleted: (state) => {
      state.completed = true;
      state.pendingReview = false;
    },
  },
});

export const {
  setReviewConfig,
  setReviewFlowConfig,
  hydrateReviewState,
  resetReviewState,
  registerHappyMoment,
  registerTriggerHit,
  clearPendingReview,
  blockReview,
  unblockReview,
  markShown,
  setSentiment,
  markCompleted,
} = reviewSlice.actions;

export default reviewSlice.reducer;
